import * as React from 'react';

import { cn } from '../../../lib/utils';

export interface InputProps extends React.ComponentProps<'input'> {
  /** Nombre del campo: sube al borde al enfocar o al tener valor */
  label?: string;
  /** Texto de ejemplo dentro del campo: solo visible al enfocar y vacío, se elimina al escribir */
  placeholder?: string;
  error?: string;
  helperText?: string;
  /** Si false, el placeholder no sube al borde (recomendado en bottom sheets). Default true. */
  floatingLabel?: boolean;
}

/**
 * Input estándar con Floating Label (Material Design).
 * - Label: nombre del campo, sube al borde al hacer click o cuando hay valor.
 * - Placeholder: texto de ejemplo, solo visible al enfocar y vacío, desaparece al escribir.
 * - Sin label explícito: usa placeholder como label flotante.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, value, placeholder, floatingLabel = true, onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const internalRef = React.useRef<HTMLInputElement>(null);
    const hasValue = Boolean(value && value.toString().length > 0);

    // Label flotante: solo si floatingLabel=true; label explícito O placeholder (si no hay label)
    const effectiveLabel = floatingLabel
      ? (label ?? (typeof placeholder === 'string' && placeholder ? placeholder : undefined))
      : undefined;
    // Placeholder: texto de ejemplo; solo visible al enfocar y vacío (solo cuando hay label)
    const inputPlaceholder = label && placeholder
      ? (isFocused && !hasValue ? placeholder : '')
      : (effectiveLabel ? '' : placeholder);

    React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const isLabelFloating = effectiveLabel ? (isFocused || hasValue) : false;
    const labelRef = React.useRef<HTMLLabelElement>(null);
    const [stripWidth, setStripWidth] = React.useState(0);
    React.useEffect(() => {
      if (isLabelFloating && labelRef.current) {
        const w = labelRef.current.offsetWidth;
        setStripWidth(w + 8); // padding para que la tira cubra bien
      }
    }, [isLabelFloating, effectiveLabel, props.required]);

    // Detectar tamaño desde className (si se pasa desde InputShowroom)
    const hasXS = className?.includes('h-6') || className?.includes('min-h-[24px]');
    const hasSM = className?.includes('h-8') || className?.includes('min-h-[32px]');
    const hasLG = className?.includes('h-12') || className?.includes('min-h-[48px]');
    const hasXL = className?.includes('h-14') || className?.includes('min-h-[56px]');
    
    // Determinar tamaño base (default es MD si no se especifica)
    const baseSize = hasXS ? 'xs' : hasSM ? 'sm' : hasLG ? 'lg' : hasXL ? 'xl' : 'md';
    
    // Clases base por tamaño - altura compacta
    const sizeClasses = {
      xs: 'h-7 min-h-[28px] px-2.5 py-1.5 text-sm leading-snug',
      sm: 'h-9 min-h-[36px] px-3 py-1.5 text-sm leading-normal',
      md: 'h-10 min-h-[40px] px-3 py-2 text-sm leading-normal',
      lg: 'h-11 min-h-[44px] px-3 py-2 text-base leading-normal',
      xl: 'h-12 min-h-[48px] px-4 py-2 text-base leading-normal',
    };
    
    // Si className ya tiene clases de tamaño, no aplicar sizeClasses
    const hasSizeClasses = hasXS || hasSM || hasLG || hasXL || className?.includes('h-10');

    // Sin label ni placeholder: estilo tradicional (casos raros como file input)
    if (!effectiveLabel) {
      return (
        <div className="relative w-full">
          <input
            ref={internalRef}
            type={props.type}
            className={cn(
              'flex w-full rounded-md border border-input bg-background ring-offset-background file:border-0 file:bg-transparent file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              !hasSizeClasses && sizeClasses[baseSize],
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
            value={value}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          {error && <p className="mt-1.5 text-sm sm:text-xs text-destructive">{error}</p>}
          {helperText && !error && <p className="mt-1.5 text-sm sm:text-xs text-muted-foreground">{helperText}</p>}
        </div>
      );
    }

    // Floating label: borde interrumpido por el texto (--------ejemplo-------)
    return (
      <div className="relative w-full">
        <div className="relative">
          <input
            ref={internalRef}
            type={props.type}
            className={cn(
              'peer w-full bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200',
              !hasSizeClasses && sizeClasses[baseSize],
              error && 'border-destructive focus-visible:ring-destructive',
              !error && 'border-input',
              className
            )}
            value={value}
            placeholder={inputPlaceholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          {/* Tira que interrumpe la línea: transparente para no mostrar fondo */}
          {isLabelFloating && effectiveLabel && (
            <div
              className={cn(
                'absolute left-3 z-[5] bg-transparent',
                isFocused ? 'top-[-4px] h-[6px]' : 'top-0 h-[2px]'
              )}
              style={{
                width: stripWidth ? `${stripWidth}px` : `calc(${(effectiveLabel.length + (props.required ? 2 : 0)) * 0.55}ch + 8px)`,
              }}
            />
          )}
          {/* Label: en el borde cuando sin foco, en el ring de foco cuando seleccionado */}
          <label
            ref={labelRef}
            className={cn(
              'absolute left-3 pointer-events-none transition-all duration-200 bg-transparent px-1 z-10 leading-none',
              isLabelFloating
                ? isFocused
                  ? 'top-[-3px] -translate-y-1/2 text-xs'
                  : 'top-0 -translate-y-1/2 text-xs'
                : 'top-1/2 -translate-y-1/2 text-sm',
              isFocused && !error && 'text-primary',
              !isFocused && !error && 'text-muted-foreground',
              error && 'text-destructive'
            )}
          >
            {effectiveLabel}
            {props.required && <span className="text-destructive ml-0.5">*</span>}
          </label>
        </div>

        {/* Helper text o error */}
        {(helperText || error) && (
          <p
            className={cn(
              'mt-1.5 text-sm sm:text-xs',
              error ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };




